import { API_ENDPOINT } from "@cede/utils";
import { clientConfig } from "@/lib/config/client-config";
import { apiHelper } from "@/utils/api";
import * as Sentry from "@sentry/nextjs";

/**
 * Whether this visitor is somewhere that requires asking before a tag loads.
 *
 * The answer comes from the country the edge resolved, which the browser cannot
 * see, so it takes one request. Nothing non-essential loads while it is in flight.
 */

/**
 * Preview and local deployments sit behind no edge, so the country header is
 * absent and every tester is treated as European. Without an override the
 * outside-the-EU path is the one path nobody can exercise before release.
 */
const REGION_OVERRIDE_PARAM = "__consent_region";

/**
 * Whether this deployment asks at all.
 *
 * Only production loads an advertising or analytics tag, so anywhere else the
 * banner would be asking permission for something that cannot happen. Set
 * `NEXT_PUBLIC_CONSENT_BANNER_FORCE` on a preview to exercise it there.
 */
/**
 * Whether this deployment loads third-party tags at all.
 *
 * The banner and the tags read this same value, so they cannot drift into the
 * state that would actually matter: tags loading with no prompt in front of them.
 */
export function tagsLoadInThisEnv(): boolean {
  return clientConfig.nodeEnv === "production";
}

/**
 * The real hosts, checked alongside the environment label.
 *
 * The label is a build-time variable set in a dashboard, so it can be wrong on a
 * production build and would then suppress the banner while Mixpanel and session
 * replay still start. The hostname cannot be wrong about where it is running.
 */
function onLiveHost(): boolean {
  const { hostname } = window.location;
  return hostname === "cedehub.io" || hostname.endsWith(".cedehub.io");
}

export function isConsentAskedHere(): boolean {
  return tagsLoadInThisEnv() || onLiveHost() || clientConfig.consentBannerForce;
}

function overriddenRegion(): boolean | null {
  if (clientConfig.nodeEnv === "production") return null;

  const override = new URLSearchParams(window.location.search).get(REGION_OVERRIDE_PARAM);
  if (override === "eu") return true;
  if (override === "row") return false;
  return null;
}

export async function isConsentRequiredHere(): Promise<boolean> {
  const override = overriddenRegion();
  if (override !== null) return override;

  try {
    const { requiresConsent } = await apiHelper.getOrThrow(API_ENDPOINT.CONSENT_REGION);
    return requiresConsent;
  } catch (error) {
    // The ask path is destructive: it clears `_ga`/`_gcl`/`_fbp`/`mp_` before the
    // banner renders, so a visitor we failed to place loses their acquisition
    // identity whatever they then click, and a refusal stands for 180 days. A
    // lookup failure is not evidence of an EU visitor, and treating it as one
    // spends that cost on a guess. Still reported: the failure is real, and this
    // is the only signal that anyone was granted without being placed.
    Sentry.captureException(error, { extra: { reason: "consent region lookup failed, granting without asking" } });
    return false;
  }
}
